<template>
  <page-section :title="$t('ProfileAuditLog_Title')">
    <rest-list :payload="fetchEndpoint">
      <rest-list-column-date
        prop="create_time"
        :title="$t('ProfileAuditLog_Field_Date')"
        :width="200"
      />

      <rest-list-column-enum
        prop="action"
        :title="$t('ProfileAuditLog_Field_Action')"
        :enum="AuditLogAction"
        :sortable="false"
      />

      <rest-list-column
        prop="field"
        :title="$t('ProfileAuditLog_Field_Field')"
        :sortable="false"
      />

      <rest-list-column
        prop="value"
        :title="$t('ProfileAuditLog_Field_Value')"
        :sortable="false"
      >
        <template v-slot="scope">
          <template
            v-if="
              subscriptionFields.includes(scope.row.field) ||
                scope.row.subscription_id
            "
          >
            {{ $t(formatSubscriptionField(parseInt(scope.row.value))) }}
          </template>
          <template v-else-if="addressabilityFields.includes(scope.row.field)">
            {{ $t(formatAddressabilityField(parseInt(scope.row.value))) }}
          </template>
          <el-tooltip
            v-else-if="scope.row.anonymised"
            :open-delay="600"
            placement="top"
            :content="$t('ProfileAuditLog_Value_Anonymised_Tooltip')"
          >
            <p>{{ $t('ProfileAuditLog_Value_Anonymised') }}</p>
          </el-tooltip>
          <template v-else-if="scope.row.action === 'reject'">
            {{ scope.row.original }}
          </template>
          <template v-else>{{ scope.row.value }}</template>
        </template>
      </rest-list-column>

      <rest-list-column-enum
        prop="source"
        :title="$t('ProfileAuditLog_Field_Source')"
        :enum="AuditLogSource"
        :sortable="false"
      />

      <rest-list-column
        prop="updated_by"
        :title="$t('ProfileAuditLog_Field_UpdatedBy')"
        :filterable="false"
        :sortable="false"
        :filter-input="false"
      />

      <el-table-column :sortable="false" class-name="table-header">
        <template slot="header" slot-scope="{}">
          <span>
            {{ $t('ProfileAuditLog_Field_Payload') }}
          </span>
        </template>
        <template slot-scope="scope">
          <el-button
            type="primary"
            plain
            size="mini"
            v-if="isValidJson(scope.row.payload)"
            @click="handlePayloadClick(scope.row)"
          >
            {{ $t('ProfilesAuditLog_JSON_Button_title') }}
            <el-icon name="view" />
          </el-button>
          <template v-else>{{ scope.row.payload }}</template>
        </template>
      </el-table-column>
    </rest-list>

    <el-dialog
      :title="$t('ProfilesEvents_JSON_Data_Title')"
      :visible="dataPopup"
      :modal="false"
      :modal-append-to-body="false"
      :before-close="closeDataPopup"
    >
      <vue-json-pretty :data="jsonData" class="json-wrapper">
        <template #nodeValue="{ node, defaultValue }">
          <template v-if="node.key === 'merge.log_id'">
            <a style="cursor: pointer" @click="openMergeHistory(node.content)">
              {{ $t('ProfileAuditLog_Payload_' + node.key.replace('.', '_')) }}
            </a>
          </template>
          <template v-else-if="node.key === 'source_profile_id'">
            <router-link
              style="cursor: pointer"
              target="_blank"
              :to="profileRoute(node.content)"
            >
              {{ defaultValue }}
            </router-link>
          </template>
          <template v-else>{{ defaultValue }}</template>
        </template>
      </vue-json-pretty>
    </el-dialog>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import RestList from '@/common/components/rest/RestList'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumn from '@/common/components/rest/RestListColumn'
import {
  AuditLogAction,
  AuditLogSource
} from '@/modules/profiles/enum/AuditLog'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import enquoteBigNumber from '@/common/helpers/JSONBigIntTransfromer'
import VueJsonPretty from 'vue-json-pretty'

export default {
  name: 'ProfileAuditLog',

  components: {
    RestListColumnDate,
    RestListColumn,
    RestListColumnEnum,
    RestList,
    PageSection,
    VueJsonPretty
  },

  data() {
    return {
      dataPopup: false,
      jsonData: null,
      AuditLogAction,
      AuditLogSource
    }
  },

  computed: {
    fetchEndpoint() {
      let params = {}
      if (window.localStorage.getItem('audit_log_filter_sub_id')) {
        params = {
          subscription_id: window.localStorage.getItem(
            'audit_log_filter_sub_id'
          )
        }
        window.localStorage.removeItem('audit_log_filter_sub_id')
      }

      if (this.readFromClickhouse) {
        return projectEndpoint({
          method: 'get',
          url: `profiles/${this.$route.query.profile_num}/attribute-track`,
          params: params
        })
      }
      return projectEndpoint({
        method: 'get',
        url: `profiles/${this.$route.query.profile_id}/auditlog`,
        params: params
      })
    },

    subscriptionFields() {
      return [
        'email_subscription',
        'push_subscription',
        'sms_subscription',
        'whatsapp_subscription'
      ]
    },

    addressabilityFields() {
      return ['email_addressable', 'sms_addressable', 'whatsapp_addressable']
    },
    readFromClickhouse() {
      return (
        this.$store.state.app.features
          .shouldReadAttributeTracksFromClickhouse === true
      )
    }
  },

  methods: {
    formatAddressabilityField(value) {
      if (value === 1) {
        return 'ProfileAuditLog_Value_Addressable'
      }

      if (value === 2) {
        return 'ProfileAuditLog_Value_NotProvided'
      }

      return 'ProfileAuditLog_Value_Not_Addressable'
    },

    formatSubscriptionField(value) {
      if (value === 1) {
        return 'ProfileAuditLog_Value_Subscribed'
      }

      if (value === 2) {
        return 'ProfileAuditLog_Value_NotProvided'
      }

      return 'ProfileAuditLog_Value_Unsubscribed'
    },
    isValidJson(value) {
      try {
        if (JSON.parse(JSON.stringify(value))) {
          return true
        }
      } catch (e) {
        return false
      }
      return false
    },
    handlePayloadClick(row) {
      if (
        row.action === 'merge' &&
        Object.hasOwn(row.payload, 'merge.log_id')
      ) {
        this.openMergeHistory(row.payload['merge.log_id'])
        return
      }

      this.showDataPopup(row.payload)
    },
    showDataPopup(json) {
      this.jsonData = JSON.parse(enquoteBigNumber(JSON.stringify(json)))
      this.dataPopup = true
    },
    openMergeHistory(id) {
      this.$router.push({
        name: 'ProfileMergeHistory',
        query: {
          project_id: this.$route.query.project_id,
          profile_num: this.$route.query.profile_num,
          profile_id: this.$route.query.profile_id,
          filters: `{"id":${id}}`
        }
      })
    },
    profileRoute(id) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: id
        }
      })
    },
    closeDataPopup() {
      this.dataPopup = false
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfileAuditLog.vue